import { Component } from "@theme/component";
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from "@theme/utilities";
import { Scroller, scrollIntoView } from "@theme/scrolling";
import { SlideshowSelectEvent } from "@theme/events";

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ["initial-slide"];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === "initial-slide" && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute("slide-id");
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ["scroller"];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1
      ? this.#setupSlideshowWithoutControls()
      : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener("mousedown", this.#handleMouseDown);
      scroller.removeEventListener("pointerdown", this.#handleMouseDown);
      this.#scroll.destroy();
    }

    this.#teardownClones();

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener("mouseenter", this.suspend);
      this.removeEventListener("mouseleave", this.resume);
      this.removeEventListener("pointerenter", this.#handlePointerEnter);
      document.removeEventListener(
        "visibilitychange",
        this.#handleVisibilityChange,
      );
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest("slideshow-component") !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (!this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // A wrap-around is still gliding onto its clone: finish it now (instant jump to
    // the real slide) so the new scroll starts from a real slide. Otherwise the
    // deferred jump would fire mid-way through this new scroll — Scroller resolves
    // `finished` whenever ANY new scroll starts — and leave it between slides.
    this.#flushPendingWrap();

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute("reveal")) {
        slide.removeAttribute("reveal");
        slide.setAttribute("aria-hidden", "true");
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === "number") return input;
      if (typeof input === "string") return parseInt(input, 10);
      if ("id" in input) {
        const requestedSlide = this.refs.slides.find(
          (slide) => slide.getAttribute("slide-id") == input.id,
        );

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute("hidden")) {
          requestedSlide.setAttribute("reveal", "");
          requestedSlide.setAttribute("aria-hidden", "false");
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (
      !slides?.length ||
      requestedIndex === undefined ||
      isNaN(requestedIndex)
    )
      return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite)
      requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const instant = prefersReducedMotion() || !animate;

    // For wrap-arounds, scroll to the pre-built clone instead of the real slide.
    // The clone is positioned just beyond the real boundary so the scroll container
    // never hits its edge — no rubber-band, no DOM mutation, no timing races.
    // After the animation lands on the clone we instant-jump to the real slide;
    // because the clone looks identical, the jump is imperceptible.
    const isWrapForward =
      !instant && this.infinite && requestedIndex > lastIndex;
    const isWrapBackward = !instant && this.infinite && requestedIndex < 0;

    const slide = isWrapForward
      ? this.#cloneFirst
      : isWrapBackward
        ? this.#cloneLast
        : slides[index];

    if (!slide) return;

    const previousIndex = this.current;

    if (!slide.hasAttribute("data-clone")) {
      slide.setAttribute("aria-hidden", "false");
    }

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = index;

    // After the smooth scroll lands on the clone, instant-jump to the real slide.
    // The token ensures only the newest pending jump can run (see #flushPendingWrap).
    if ((isWrapForward || isWrapBackward) && !instant) {
      const token = { index };
      this.#pendingWrap = token;
      requestAnimationFrame(() => {
        this.#scroll.finished.then(async () => {
          if (this.#pendingWrap !== token) return;
          // `finished` only means "no scroll event for 50ms". If the page stalls
          // briefly mid-glide (heavy paint/decode) it fires before the glide has
          // reached the clone, and jumping then strands the scroller between slides.
          // Wait until the scroller has actually arrived (or 1.5s at most).
          const { scroller } = this.refs;
          const clone = isWrapForward ? this.#cloneFirst : this.#cloneLast;
          const deadline = performance.now() + 1500;
          while (
            clone &&
            Math.abs(scroller.scrollLeft - clone.offsetLeft) > 2 &&
            performance.now() < deadline
          ) {
            await new Promise((resolve) => requestAnimationFrame(resolve));
            if (this.#pendingWrap !== token) return;
          }
          this.#pendingWrap = null;
          const realSlide = slides[index];
          if (realSlide) {
            this.#scroll.to(realSlide, { instant: true });
            // Force #visibleSlides so the async #handleScroll → #sync() call that
            // follows the instant scroll event reads the correct slide index instead
            // of stale IO state, which would corrupt this.current before the next swipe.
            this.#visibleSlides = [realSlide];
          }
        });
      });
    }

    this.#centerSelectedThumbnail(index, instant ? "instant" : "smooth");

    // Always reference the real slide in the event, not a clone
    const realSlide = slides[index];
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: "select",
        slide: realSlide,
        id: realSlide?.getAttribute("slide-id"),
      }),
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(":hover") || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute("paused");
  }

  set paused(value) {
    if (value) {
      this.setAttribute("paused", "");
    } else {
      this.removeAttribute("paused");
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute("autoplay");
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) =>
        el.setAttribute("aria-selected", `${i === value}`),
      );
    }

    this.#updateProgressiveDots(value);

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next)
      next.disabled = Boolean(
        !this.infinite && slides && this.nextIndex >= slides.length,
      );
  }

  get infinite() {
    return this.getAttribute("infinite") != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute("disabled", String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute("disabled") === "true" ||
      (this.hasAttribute("mobile-disabled") && !mediaQueryLarge.matches)
    );
  }

  /**
   * Clone of the first real slide, appended after the last — provides a scroll target
   * for forward wrap-arounds so the browser never hits the scroll boundary.
   * @type {HTMLElement|null}
   */
  #cloneFirst = null;

  /**
   * Clone of the last real slide, prepended before the first — provides a scroll target
   * for backward wrap-arounds.
   * @type {HTMLElement|null}
   */
  #cloneLast = null;

  /**
   * Wrap-around whose instant jump from the clone to the real slide hasn't run yet.
   * @type {{index: number}|null}
   */
  #pendingWrap = null;

  /**
   * Runs a pending wrap-around jump immediately and cancels its deferred callback.
   */
  #flushPendingWrap() {
    const pending = this.#pendingWrap;
    if (!pending) return;
    this.#pendingWrap = null;

    const realSlide = this.slides?.[pending.index];
    if (!realSlide || !this.#scroll) return;
    this.#scroll.to(realSlide, { instant: true });
    this.#visibleSlides = [realSlide];
  }

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute("auto-hide-controls")) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute("aria-hidden", "false");
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener("mousedown", this.#handleMouseDown);
    scroller.addEventListener("pointerdown", this.#handleMouseDown);

    // Prepend/append clone slides so the scroll boundary is never reached —
    // eliminates rubber-band and all DOM-reorder timing issues.
    // After adding clones, immediately snap to the real first slide — cloneLast is
    // prepended so without this the scroll starts at position 0 (showing the last image).
    if (this.infinite) {
      this.#setupClones();
      const firstRealSlide = this.slides?.[0];
      if (firstRealSlide) this.#scroll.to(firstRealSlide, { instant: true });
    }

    this.addEventListener("mouseenter", this.suspend);
    this.addEventListener("mouseleave", this.resume);
    this.addEventListener("pointerenter", this.#handlePointerEnter);
    document.addEventListener("visibilitychange", this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute("slide-id");

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute("auto-hide-controls")) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: "scroll",
        slide,
        id: slide.getAttribute("slide-id"),
      }),
    );
  };

  #onTransitionInit = () => {
    this.setAttribute("transitioning", "");
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute("transitioning");
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    // When the scroller is sitting on a clone, map it to the real slide index
    // using scroll position rather than IntersectionObserver (clones are not observed).
    if (this.infinite && (this.#cloneFirst || this.#cloneLast)) {
      const { scroller } = this.refs;
      const scrollLeft = scroller.scrollLeft;
      const slideWidth = scroller.offsetWidth;
      if (
        this.#cloneFirst &&
        Math.abs(scrollLeft - this.#cloneFirst.offsetLeft) < slideWidth * 0.5
      ) {
        return (this.current = 0);
      }
      if (
        this.#cloneLast &&
        Math.abs(scrollLeft - this.#cloneLast.offsetLeft) < slideWidth * 0.5
      ) {
        return (this.current = slides.length - 1);
      }
    }

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint =
      visibleSlides.length > 1
        ? scroller.getBoundingClientRect()[axis]
        : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest("model-viewer")) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;
    let rawPointerDelta = 0; // Track raw pointer movement for infinite loop at boundaries
    let initialSwipeDirection = 0; // Capture swipe direction from first move for boundary detection

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener("click", preventDefault, {
          once: true,
          signal,
        });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest(
          "slideshow-component",
        );
        const isNested =
          closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        // In infinite mode, we can always move in any direction (clones absorb boundary pressure)
        const cannotMoveInDirection =
          !this.infinite &&
          ((movingRight && this.atStart) || (movingLeft && this.atEnd));

        // Capture initial swipe direction for flick detection in onPointerUp
        initialSwipeDirection = movingLeft ? 1 : movingRight ? -1 : 0;

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute("dragging", "");
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);
      rawPointerDelta = startPosition - current; // Track total pointer movement from start

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      // If the drag landed on a clone, snap to it cleanly then instant-jump to the
      // real slide. The clone looks identical so the jump is imperceptible to the user.
      const cloneInfo = this.#isOnClone();
      if (cloneInfo) {
        this.removeAttribute("dragging");
        this.releasePointerCapture(event.pointerId);
        const realSlide = slides[cloneInfo.realIndex];
        if (realSlide) {
          this.#scroll.to(realSlide, { instant: true });
          this.current = cloneInfo.realIndex;
          this.#visibleSlides = [realSlide];
        }
        this.#dragging = false;
        this.#scroll.snap = true;
        this.resume();
        return;
      }

      const { current } = this;
      const direction = Math.sign(velocity);
      const next = this.#sync();

      let modifier =
        current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10
          ? 0
          : direction;

      // In infinite mode, use swipe direction to enable boundary wrap-around.
      // Use velocity direction (final intent) as primary — it correctly captures
      // direction-change gestures where the user reversed mid-swipe. Without this,
      // rawPointerDelta or initialSwipeDirection reflects the *first* movement, and
      // a user who started backward then reversed forward would incorrectly trigger
      // a backward wrap at the first-slide boundary (and vice versa at last-slide).
      // Fall back to net displacement only when velocity is zero (slow/gentle placement).
      if (this.infinite) {
        const netDirection =
          Math.abs(rawPointerDelta) >= 20
            ? Math.sign(rawPointerDelta)
            : initialSwipeDirection;
        const swipeDirection = direction !== 0 ? direction : netDirection;
        // At boundaries, use swipe direction to trigger wrap-around
        if (swipeDirection !== 0) {
          const atLastSlide = next === slides.length - 1;
          const atFirstSlide = next === 0;
          const swipingForward = swipeDirection > 0;
          const swipingBackward = swipeDirection < 0;

          if (
            (atLastSlide && swipingForward) ||
            (atFirstSlide && swipingBackward)
          ) {
            modifier = swipeDirection;
          }
        }
      }

      let newIndex = next + modifier;
      const isWrapping =
        this.infinite && (newIndex < 0 || newIndex >= slides.length);

      // Handle infinite looping or clamping
      if (this.infinite) {
        if (newIndex < 0) newIndex = slides.length - 1;
        else if (newIndex >= slides.length) newIndex = 0;
      } else {
        newIndex = clamp(newIndex, 0, slides.length - 1);
      }

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.removeAttribute("dragging");
      this.releasePointerCapture(event.pointerId);

      if (isWrapping) {
        // Animate briefly to the clone first (Amazon-style "rush through"), then teleport to
        // the real slide. This gives the browser a clean scroll-end state before the instant
        // jump — without it the browser's snap/scroll state is unsettled after the teleport,
        // causing the first post-wrap drag to do nothing (the "double swipe" bug).
        // touch-action: none (set on slideshow-component) prevents iOS native momentum from
        // racing with this animation, which was the original cause of the deadlock.
        const targetClone = newIndex === 0 ? this.#cloneFirst : this.#cloneLast;
        const realSlide = slides[newIndex];

        if (targetClone) {
          this.#scroll.to(targetClone);
          await Promise.race([
            this.#scroll.finished,
            new Promise((resolve) => setTimeout(resolve, 600)),
          ]);
        }

        // If a new drag started during the animation, let that drag handle navigation
        if (this.#dragging) return;

        if (realSlide) {
          this.#scroll.to(realSlide, { instant: true });
          this.current = newIndex;
          this.#visibleSlides = [realSlide];
          this.#centerSelectedThumbnail(newIndex);
        }

        // Wait for the instant-jump's scroll event and Scroller debounce to fully settle
        // before re-enabling snap. The instant jump fires a scroll event with #ignore=true,
        // which triggers #reset() → #handleScrollEnd debounced. Calling snap=true immediately
        // races with this debounce, leaving #isScrolling=true and causing the browser to
        // resist smooth scrolling on the first post-wrap drag ("double swipe" bug).
        // 50ms matches the Scroller's SCROLL_END_TIMEOUT so state is fully clean.
        await new Promise((resolve) => setTimeout(resolve, 50));
        if (this.#dragging) return;

        this.#scroll.snap = true;
        this.resume();
        if (realSlide) {
          this.dispatchEvent(
            new SlideshowSelectEvent({
              index: newIndex,
              previousIndex: currentIndex,
              userInitiated: true,
              trigger: "drag",
              slide: realSlide,
              id: realSlide.getAttribute("slide-id"),
            }),
          );
        }
        return;
      }

      this.#scroll.to(newSlide);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: "drag",
          slide: newSlide,
          id: newSlide.getAttribute("slide-id"),
        }),
      );

      this.current = newIndex;

      await Promise.race([
        this.#scroll.finished,
        new Promise((resolve) => setTimeout(resolve, 600)),
      ]);

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener("pointermove", onPointerMove, { signal });
    document.addEventListener("pointerup", onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener("pointerdown", onPointerUp, { signal });
    document.addEventListener("pointercancel", onPointerUp, { signal });
    document.addEventListener("pointercapturelost", onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute("actioned", "");
  };

  get slides() {
    return this.refs.slides?.filter(
      (slide) =>
        (!slide.hasAttribute("hidden") || slide.hasAttribute("reveal")) &&
        !slide.hasAttribute("data-clone"),
    );
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute("initial-slide");
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Prepend a clone of the last slide and append a clone of the first slide.
   * This means the scroll container always has a real slide on both sides, so
   * the native scroll boundary is never reached and rubber-band cannot occur.
   * After the user scrolls onto a clone, #isOnClone() detects it and we
   * instant-jump to the corresponding real slide (imperceptible since identical).
   */
  #setupClones() {
    const { slides } = this;
    if (!slides || slides.length < 2) return;

    this.#teardownClones();

    const first = slides[0];
    const last = slides[slides.length - 1];

    this.#cloneFirst = /** @type {HTMLElement} */ (first.cloneNode(true));
    this.#cloneFirst.setAttribute("data-clone", "first");
    this.#cloneFirst.setAttribute("aria-hidden", "true");
    this.#cloneFirst.removeAttribute("slide-id");
    this.#cloneFirst.removeAttribute("data-ref"); // exclude from refs.slides live NodeList

    this.#cloneLast = /** @type {HTMLElement} */ (last.cloneNode(true));
    this.#cloneLast.setAttribute("data-clone", "last");
    this.#cloneLast.setAttribute("aria-hidden", "true");
    this.#cloneLast.removeAttribute("slide-id");
    this.#cloneLast.removeAttribute("data-ref"); // exclude from refs.slides live NodeList

    first.before(this.#cloneLast); // prepend clone-of-last before real first
    last.after(this.#cloneFirst); // append clone-of-first after real last
  }

  /** Remove clones from the DOM and clear references. */
  #teardownClones() {
    this.#cloneFirst?.remove();
    this.#cloneLast?.remove();
    this.#cloneFirst = null;
    this.#cloneLast = null;
  }

  /**
   * Returns clone info if the scroller is currently centred on a clone slide,
   * or null if we are on a real slide.
   * @returns {{ clone: HTMLElement, realIndex: number } | null}
   */
  #isOnClone() {
    if (!this.infinite || (!this.#cloneFirst && !this.#cloneLast)) return null;
    const { scroller } = this.refs;
    const scrollLeft = scroller.scrollLeft;
    const slideWidth = scroller.offsetWidth;
    if (
      this.#cloneFirst &&
      Math.abs(scrollLeft - this.#cloneFirst.offsetLeft) < slideWidth * 0.5
    ) {
      return { clone: this.#cloneFirst, realIndex: 0 };
    }
    if (
      this.#cloneLast &&
      Math.abs(scrollLeft - this.#cloneLast.offsetLeft) < slideWidth * 0.5
    ) {
      return {
        clone: this.#cloneLast,
        realIndex: (this.slides?.length ?? 1) - 1,
      };
    }
    return null;
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () =>
    document.hidden ? this.pause() : this.resume();

  #updateControlsVisibility() {
    if (!this.hasAttribute("auto-hide-controls")) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver
            ? this.#intersectionObserver.takeRecords()
            : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible =
            entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort(
          (a, b) => slides.indexOf(a) - slides.indexOf(b),
        );
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: "1px",
      },
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = "smooth") {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement))
      return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement))
      return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: "center",
      inline: "center",
    });
  }

  /**
   * Updates progressive dots visibility and sizing.
   * Shows a sliding window of max visible dots centered on the active dot,
   * with dots scaling down toward the edges to indicate more content.
   * @param {number} activeIndex - The index of the currently active dot
   */
  #updateProgressiveDots(activeIndex) {
    const { dots } = this.refs;
    if (!dots?.length) return;

    const dotsList = dots[0]?.closest("[data-progressive-dots]");
    if (!dotsList) return;

    const maxVisible = parseInt(
      dotsList.getAttribute("data-max-visible") || "5",
      10,
    );
    const totalDots = dots.length;

    if (totalDots <= maxVisible) {
      dots.forEach((dot) => {
        const li = dot.closest("li");
        if (li) li.setAttribute("data-dot-scale", "1");
      });
      return;
    }

    // Calculate the visible window centered on activeIndex
    let windowStart = activeIndex - Math.floor(maxVisible / 2);
    let windowEnd = windowStart + maxVisible - 1;

    if (windowStart < 0) {
      windowStart = 0;
      windowEnd = maxVisible - 1;
    }
    if (windowEnd >= totalDots) {
      windowEnd = totalDots - 1;
      windowStart = windowEnd - maxVisible + 1;
    }

    dots.forEach((dot, i) => {
      const li = dot.closest("li");
      if (!li) return;

      if (i < windowStart || i > windowEnd) {
        li.setAttribute("data-dot-scale", "0");
      } else if (i === windowStart && windowStart > 0) {
        li.setAttribute("data-dot-scale", "0.5");
      } else if (i === windowStart + 1 && windowStart > 0) {
        li.setAttribute("data-dot-scale", "0.75");
      } else if (i === windowEnd && windowEnd < totalDots - 1) {
        li.setAttribute("data-dot-scale", "0.5");
      } else if (i === windowEnd - 1 && windowEnd < totalDots - 1) {
        li.setAttribute("data-dot-scale", "0.75");
      } else {
        li.setAttribute("data-dot-scale", "1");
      }
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute("aria-hidden", `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get("slideshow-component")) {
  customElements.define("slideshow-component", Slideshow);
}
